import type { ProdantixClient } from 'ProdantixSdk/core/client';
import { ProdantixError } from 'ProdantixSdk/core/errors';
import { SYSTEM_EVENT } from 'ProdantixSdk/core/system-events';
import type { EventProperties } from 'ProdantixSdk/core/types';

export interface AutocaptureOptions {
  captureElementText?: boolean;
  clicks?: boolean;
  maskSelectors?: readonly string[];
  onError?: (error: unknown) => void;
  pageviews?: boolean;
}

type Capturer = Pick<ProdantixClient, 'capture'>;

const OPT_OUT_ATTRIBUTE = 'data-pdx-no-capture';
const OPT_OUT_CLASS = 'pdx-no-capture';
const FORM_FIELDS = new Set(['input', 'select', 'textarea']);
const ALWAYS_MASKED_TYPES = new Set(['email', 'password', 'tel']);

interface LocationLike {
  host: string;
  href: string;
  pathname: string;
  search: string;
}

interface DocumentLike {
  addEventListener(type: string, listener: (event: unknown) => void, capture?: boolean): void;
  referrer: string;
  removeEventListener(type: string, listener: (event: unknown) => void, capture?: boolean): void;
  title: string;
}

interface HistoryLike {
  pushState(...args: unknown[]): void;
  replaceState(...args: unknown[]): void;
}

interface WindowLike {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

interface ElementLike {
  getAttribute(name: string): string | null;
  id: string;
  className: unknown;
  matches?(selector: string): boolean;
  parentElement: ElementLike | null;
  tagName: string;
  textContent: string | null;
}

function getLocation(): LocationLike | undefined {
  return (globalThis as { location?: LocationLike }).location;
}

function getDocument(): DocumentLike | undefined {
  return (globalThis as { document?: DocumentLike }).document;
}

function getWindow(): WindowLike | undefined {
  const win = (globalThis as { window?: WindowLike }).window;
  return win ?? globalThis;
}

function isElement(value: unknown): value is ElementLike {
  return typeof value === 'object' && value !== null && typeof (value as { tagName?: unknown }).tagName === 'string';
}

function withoutQuery(address: string, base?: string): string | undefined {
  let url: URL;
  try {
    url = new URL(address, base);
  } catch {
    return undefined;
  }
  return url.protocol === 'http:' || url.protocol === 'https:' ? `${url.origin}${url.pathname}` : url.protocol;
}

function urlProperties(): EventProperties {
  const location = getLocation();
  const doc = getDocument();
  const props: EventProperties = {};
  if (location) {
    const current = withoutQuery(location.href);
    if (current) {
      props.$current_url = current;
    }
    props.$pathname = location.pathname;
    props.$host = location.host;
  }
  if (doc) {
    if (doc.title) {
      props.$title = doc.title;
    }
    const referrer = doc.referrer ? withoutQuery(doc.referrer) : undefined;
    if (referrer) {
      props.$referrer = referrer;
    }
  }
  return props;
}

function classesOf(element: ElementLike): string {
  return typeof element.className === 'string' ? element.className : '';
}

function isFormField(element: ElementLike): boolean {
  return FORM_FIELDS.has(element.tagName.toLowerCase()) || ALWAYS_MASKED_TYPES.has((element.getAttribute('type') ?? '').toLowerCase());
}

function elementProperties(element: ElementLike, isMasked: boolean, captureText: boolean): EventProperties {
  const props: EventProperties = {
    $el_tag: element.tagName.toLowerCase(),
    $event_type: 'click',
  };
  if (isMasked) {
    return props;
  }
  const text = captureText ? element.textContent?.trim() : undefined;
  if (text) {
    props.$el_text = text.slice(0, 255);
  }
  if (element.id) {
    props.$el_id = element.id;
  }
  const classes = classesOf(element);
  if (classes) {
    props.$el_classes = classes;
  }
  const href = element.getAttribute('href');
  const stripped = href ? withoutQuery(href, getLocation()?.href) : undefined;
  if (stripped) {
    props.$el_href = stripped;
  }
  return props;
}

function createMaskCheck(selectors: readonly string[], onError: (error: unknown) => void): (element: ElementLike) => boolean {
  const broken = new Set<string>();

  const matchesSelector = (element: ElementLike, selector: string): boolean => {
    if (broken.has(selector) || !element.matches) {
      return false;
    }
    try {
      return element.matches(selector);
    } catch (error) {
      broken.add(selector);
      onError(new ProdantixError('config', `autocapture maskSelectors entry "${selector}" is not a valid selector`, { cause: error }));
      return false;
    }
  };

  const isMarked = (element: ElementLike): boolean => element.getAttribute(OPT_OUT_ATTRIBUTE) !== null || classesOf(element).split(/\s+/).includes(OPT_OUT_CLASS) || isFormField(element) || selectors.some(selector => matchesSelector(element, selector));

  return element => {
    for (let node: ElementLike | null = element; node; node = node.parentElement) {
      if (isMarked(node)) {
        return true;
      }
    }
    return false;
  };
}

function installPageviews(client: Capturer): () => void {
  const history = (globalThis as { history?: HistoryLike }).history;
  const win = getWindow();
  let lastKey = '';

  const capture = (): void => {
    const location = getLocation();
    const key = location ? `${location.pathname}${location.search}` : '';
    if (key === lastKey) {
      return;
    }
    lastKey = key;
    client.capture(SYSTEM_EVENT.pageview, { properties: urlProperties() });
  };

  capture();

  if (!history || !win) {
    return () => undefined;
  }
  const originalPush = history.pushState.bind(history);
  const originalReplace = history.replaceState.bind(history);
  history.pushState = (...args: unknown[]): void => {
    originalPush(...args);
    capture();
  };
  history.replaceState = (...args: unknown[]): void => {
    originalReplace(...args);
    capture();
  };
  win.addEventListener('popstate', capture);

  return () => {
    history.pushState = originalPush;
    history.replaceState = originalReplace;
    win.removeEventListener('popstate', capture);
  };
}

function installClicks(client: Capturer, options: AutocaptureOptions): () => void {
  const doc = getDocument();
  if (!doc) {
    return () => undefined;
  }
  const isMasked = createMaskCheck(options.maskSelectors ?? [], options.onError ?? (() => undefined));
  const captureText = options.captureElementText ?? false;
  const handler = (event: unknown): void => {
    const target = (event as { target?: unknown }).target;
    if (!isElement(target)) {
      return;
    }
    client.capture(SYSTEM_EVENT.autocapture, {
      properties: { ...urlProperties(), ...elementProperties(target, isMasked(target), captureText) },
    });
  };
  doc.addEventListener('click', handler, true);
  return () => doc.removeEventListener('click', handler, true);
}

export function installAutocapture(client: Capturer, options: AutocaptureOptions = {}): () => void {
  const cleanups: Array<() => void> = [];
  if (options.pageviews ?? true) {
    cleanups.push(installPageviews(client));
  }
  if (options.clicks ?? true) {
    cleanups.push(installClicks(client, options));
  }
  return () => {
    for (const cleanup of cleanups) {
      cleanup();
    }
  };
}
